import { useEffect, useMemo, useState, type FC } from "react";
import { useT } from "../../features/i18n/useT";

type Route = "/" | "/deadlines" | "/schedule" | "/map" | "/settings";

function go(path: Route) {
  if (window.location.pathname === path) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("app:navigate"));
}

function usePath() {
  const [p, setP] = useState<Route>(() => {
    const path = window.location.pathname;
    if (path === "/" || path === "") return "/";
    if (path === "/schedule") return "/schedule";
    if (path === "/deadlines") return "/deadlines";
    if (path === "/map") return "/map";
    if (path === "/settings") return "/settings";
    return "/";
  });

  useEffect(() => {
    const on = () => {
      const path = window.location.pathname;
      let newPath: Route = "/";
      if (path === "/" || path === "") newPath = "/";
      else if (path === "/schedule") newPath = "/schedule";
      else if (path === "/deadlines") newPath = "/deadlines";
      else if (path === "/map") newPath = "/map";
      else if (path === "/settings") newPath = "/settings";
      setP(newPath);
    };
    window.addEventListener("popstate", on);
    window.addEventListener("app:navigate", on);
    return () => {
      window.removeEventListener("popstate", on);
      window.removeEventListener("app:navigate", on);
    };
  }, []);

  return p;
}

function isActive(path: string, route: Route) {
  if (route === "/") return path === "/";
  return path === route;
}

function IcHome() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"
        fill="currentColor"
      />
    </svg>
  );
}

function IcDeadlines() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 3a1 1 0 0 1 1 1v5.2l3.4 2a1 1 0 1 1-1 1.7l-3.9-2.3A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IcSchedule() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1Zm12 8H5v10h14V10Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IcMap() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20 5.5 16 4l-8 2.5L4 5v15.5l4 1.5 8-2.5 4 1.5V5.5ZM8 19.2 6 18.5V7.8l2 .7v10.7Zm8 0-6 1.9V8.5l6-1.9v12.6Zm2-.7-2-.7V6.6l2 .7v11.9Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IcSettings() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M19.14 12.94a7.8 7.8 0 0 0 .06-.94 7.8 7.8 0 0 0-.06-.94l2.03-1.58a.8.8 0 0 0 .19-1.02l-1.92-3.32a.8.8 0 0 0-.96-.36l-2.39.96a7.6 7.6 0 0 0-1.63-.94l-.36-2.54A.8.8 0 0 0 13.3 1h-3.6a.8.8 0 0 0-.79.67l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.8.8 0 0 0-.96.36L1.65 7.87a.8.8 0 0 0 .19 1.02l2.03 1.58a7.8 7.8 0 0 0-.06.94c0 .32.02.63.06.94l-2.03 1.58a.8.8 0 0 0-.19 1.02l1.92 3.32a.8.8 0 0 0 .96.36l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54a.8.8 0 0 0 .79.67h3.6a.8.8 0 0 0 .79-.67l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96a.8.8 0 0 0 .96-.36l1.92-3.32a.8.8 0 0 0-.19-1.02l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function BottomNav() {
  const t = useT();
  const path = usePath();

  const items: Array<{
    route: Route;
    label: string;
    Icon: FC;
  }> = useMemo(
    () => [
      { route: "/", label: t("nav.home"), Icon: IcHome },
      { route: "/deadlines", label: t("nav.deadlines"), Icon: IcDeadlines },
      { route: "/schedule", label: t("nav.schedule"), Icon: IcSchedule },
      { route: "/map", label: t("nav.map"), Icon: IcMap },
      { route: "/settings", label: t("nav.settings"), Icon: IcSettings }
    ],
    [t]
  );

  return (
    <nav className="bottom-nav" aria-label={t("common.navigation")}>
      <div className="bottom-nav-inner">
        {items.map(({ route, label, Icon }) => {
          const active = isActive(path, route);
          return (
            <button
              key={route}
              className={`nav-item ${active ? "is-active" : ""}`}
              onClick={() => go(route)}
              aria-label={label}
            >
              <span className="nav-ic" aria-hidden="true">
                <Icon />
              </span>
              <span className="nav-dot" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </nav>
  );
} 
